import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  LinearProgress,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import NotificationSystem from '../components/NotificationSystem';
import { useNotification } from '../hooks/useNotification';
import { useNotifications } from '../hooks/useNotifications';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudSync as CloudSyncIcon,
  PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { useAuth } from '../context/AuthContext';

interface User {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'user';
  title?: string;
  mobile?: string;
  show_title?: boolean;
  created_at: string;
  updated_at: string;
}

const Users: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingGoogleWorkspace, setSyncingGoogleWorkspace] = useState(false);
  const [syncingPhotos, setSyncingPhotos] = useState(false);
  const [syncingUserIds, setSyncingUserIds] = useState<Set<number>>(new Set());
  
  // Estados para barras de progresso
  const [syncProgress, setSyncProgress] = useState(0);
  const [googleWorkspaceProgress, setGoogleWorkspaceProgress] = useState(0);
  const [photosProgress, setPhotosProgress] = useState(0);
  
  // Sistema de notificações padronizado
  const { notification, showSuccess, showError, showInfo } = useNotification();
  const { addNotification, refreshNotifications } = useNotifications();

  // Estados para o modal de edição
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    title: '',
    mobile: '',
    role: 'user' as 'admin' | 'user',
    show_title: true
  });
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
          const token = localStorage.getItem('authToken');
    const response = await fetch('/api/users', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
      
      if (!response.ok) {
        throw new Error('Erro ao carregar usuários');
      }
      
      const data = await response.json();
      // Garantir que show_title tenha um valor padrão se não vier da API
      const usersWithShowTitle = data.map((user: User) => ({
        ...user,
        show_title: user.show_title !== undefined ? user.show_title : true
      }));
      setUsers(usersWithShowTitle);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      showError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    setSyncProgress(0);
    
    try {
      // Iniciar com progresso indeterminado (mais realista)
      setSyncProgress(-1); // -1 indica progresso indeterminado

      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/users/sync-all-signatures', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Erro ao sincronizar assinaturas');
      }
      
      const result = await response.json();
      showSuccess('Sincronização concluída com sucesso!');
      
      // Mostrar 100% apenas quando realmente terminar
      setSyncProgress(100);
      
      // Reset progresso após 1 segundo
      setTimeout(() => setSyncProgress(0), 1000);
    } catch (error) {
      console.error('Erro na sincronização:', error);
      showError('Erro ao sincronizar assinaturas');
      setSyncProgress(0);
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncUser = async (userId: number) => {
    // Adicionar o userId ao Set de sincronização
    setSyncingUserIds(prev => new Set(prev).add(userId));
    
    try {
      const user = users.find(u => u.id === userId);
      
      if (!user) {
        showError('Usuário não encontrado');
        return;
      }

      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/unified-signatures/apply/${encodeURIComponent(user.email)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Erro ao sincronizar assinatura');
      }
      
      const result = await response.json();
      showSuccess('Assinatura sincronizada com sucesso!');
    } catch (error) {
      console.error('Erro na sincronização:', error);
      showError('Erro ao sincronizar assinatura');
    } finally {
      // Remover o userId do Set de sincronização
      setSyncingUserIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleEditUser = async (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      // Preencher o formulário com os dados do usuário
      setEditForm({
        name: user.name,
        email: user.email,
        title: user.title || '',
        mobile: user.mobile || '',
        role: user.role,
        show_title: user.show_title !== undefined ? user.show_title : true
      });
      
      setEditingUser(user);
      setEditDialogOpen(true);
    } else {

      showError('Usuário não encontrado!');
    }
  };

  const handleEditFormChange = (field: string, value: string | boolean) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;

    setEditLoading(true);
    try {



      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });



      if (!response.ok) {
        throw new Error('Erro ao atualizar usuário');
      }

      const result = await response.json();


      // Atualizar a lista local
      setUsers(users.map(u => 
        u.id === editingUser.id 
          ? { ...u, ...editForm }
          : u
      ));

      showSuccess('Usuário atualizado com sucesso!');

      setEditDialogOpen(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      showError('Erro ao atualizar usuário');
    } finally {
      setEditLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingUser(null);
    setEditForm({
      name: '',
      email: '',
      title: '',
      mobile: '',
      role: 'user',
      show_title: true
    });
  };

  const handleSyncGoogleWorkspace = async () => {
    setSyncingGoogleWorkspace(true);
    setGoogleWorkspaceProgress(0);
    
    try {
      // Iniciar com progresso indeterminado
      setGoogleWorkspaceProgress(-1);

      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/google-workspace/sync-users', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          domain: 'acspro.com.br' // Usando o domínio principal
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.needsAuth) {
          showInfo('Autenticação com Google necessária. Entre em contato com o administrador.');
          return;
        }
        throw new Error('Erro ao sincronizar com Google Workspace');
      }
      
      const result = await response.json();
      showSuccess(`Sincronização com Google Workspace concluída! ${result.synced || 0} usuários sincronizados.`);

              // Adicionar notificação
        addNotification({
          title: 'Sincronização concluída',
          message: `${result.synced || 0} usuários foram sincronizados do Google Workspace com sucesso`,
          type: 'success'
        });
        
        // Atualizar notificações do backend imediatamente
        setTimeout(() => {
          refreshNotifications();
        }, 500);

      // Recarregar a lista de usuários após a sincronização
      await loadUsers();
      
      // Mostrar 100% apenas quando realmente terminar
      setGoogleWorkspaceProgress(100);
      
      // Reset progresso após 1 segundo
      setTimeout(() => setGoogleWorkspaceProgress(0), 1000);
    } catch (error) {
      console.error('Erro na sincronização com Google Workspace:', error);
      showError('Erro ao sincronizar com Google Workspace');
      
      // Adicionar notificação de erro
      addNotification({
        title: 'Erro na sincronização',
        message: 'Falha na sincronização com Google Workspace. Verifique as configurações.',
        type: 'error'
      });
      
      setGoogleWorkspaceProgress(0);
    } finally {
      setSyncingGoogleWorkspace(false);
    }
  };

  const handleSyncPhotos = async () => {
    setSyncingPhotos(true);
    setPhotosProgress(0);
    
    try {
      // Iniciar com progresso indeterminado
      setPhotosProgress(-1);

      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/users/sync-all-photos', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Erro ao sincronizar fotos');
      }
      
      const result = await response.json();
      showSuccess(`Sincronização de fotos concluída! ${result.updated || 0} fotos atualizadas de ${result.total || 0} usuários.`);

              // Adicionar notificação
        addNotification({
          title: 'Sincronização de fotos concluída',
          message: `${result.updated || 0} fotos foram atualizadas de ${result.total || 0} usuários`,
          type: 'success'
        });
        
        // Atualizar notificações do backend imediatamente
        setTimeout(() => {
          refreshNotifications();
        }, 500);

      // Recarregar a lista de usuários após a sincronização
      await loadUsers();
      
      // Mostrar 100% apenas quando realmente terminar
      setPhotosProgress(100);
      
      // Reset progresso após 1 segundo
      setTimeout(() => setPhotosProgress(0), 1000);
    } catch (error) {
      console.error('Erro na sincronização de fotos:', error);
      showError('Erro ao sincronizar fotos');
      
      // Adicionar notificação de erro
      addNotification({
        title: 'Erro na sincronização de fotos',
        message: 'Falha na sincronização de fotos. Verifique as configurações.',
        type: 'error'
      });
      
      setPhotosProgress(0);
    } finally {
      setSyncingPhotos(false);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      if (window.confirm(`Tem certeza que deseja excluir o usuário "${user.name}"?`)) {
        try {
          const token = localStorage.getItem('authToken');
          const response = await fetch(`/api/users/${userId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (!response.ok) {
            throw new Error('Erro ao excluir usuário');
          }
          
          // Remover o usuário da lista local
          setUsers(users.filter(u => u.id !== userId));
          
          showSuccess('Usuário excluído com sucesso!');
        } catch (error) {
          console.error('Erro ao excluir usuário:', error);
          showError('Erro ao excluir usuário');
        }
      }
    }
  };



  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Nome',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: '#e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6b7280',
              fontWeight: 600,
              fontSize: '0.875rem',
              mr: 1
            }}
          >
            {params.value?.charAt(0)?.toUpperCase() || 'U'}
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: '#6b7280', fontFamily: 'monospace' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'role',
      headerName: 'Função',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value === 'admin' ? 'Admin' : 'Usuário'}
          color={params.value === 'admin' ? 'primary' : 'default'}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 24,
            '& .MuiChip-label': {
              px: 1.5,
            },
            backgroundColor: params.value === 'admin' ? '#374151' : '#e5e7eb',
            color: params.value === 'admin' ? 'white' : '#6b7280',
            '&:hover': {
              backgroundColor: params.value === 'admin' ? '#1f2937' : '#d1d5db',
            }
          }}
        />
      ),
    },
    {
      field: 'title',
      headerName: 'Cargo',
      width: 300,
      renderCell: (params) => {
        const user = params.row as User;
        const showTitle = user.show_title !== undefined ? user.show_title : true;
        
        return (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            width: '100%', 
            minWidth: 0,
            height: '100%',
            padding: '0 4px'
          }}>
            <Checkbox
              checked={showTitle}
              size="small"
              onClick={(e) => e.stopPropagation()}
              onChange={async (e) => {
                const newValue = e.target.checked;
                try {
                  const token = localStorage.getItem('authToken');
                  const response = await fetch(`/api/users/${user.id}`, {
                    method: 'PUT',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      ...user,
                      show_title: newValue
                    })
                  });
                  
                  if (response.ok) {
                    setUsers(users.map(u => 
                      u.id === user.id 
                        ? { ...u, show_title: newValue }
                        : u
                    ));
                    showSuccess('Configuração atualizada com sucesso!');
                  } else {
                    showError('Erro ao atualizar configuração');
                  }
                } catch (error) {
                  console.error('Erro ao atualizar show_title:', error);
                  showError('Erro ao atualizar configuração');
                }
              }}
              sx={{
                padding: '4px',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.2rem'
                }
              }}
            />
            <Typography variant="body2" sx={{ 
              color: params.value ? '#374151' : '#9ca3af',
              fontStyle: params.value ? 'normal' : 'italic',
              fontWeight: params.value ? 500 : 400,
              flex: 1
            }}>
              {params.value || 'Não informado'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'mobile',
      headerName: 'Telefone',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ 
          color: params.value ? '#374151' : '#9ca3af',
          fontStyle: params.value ? 'normal' : 'italic',
          fontWeight: params.value ? 500 : 400,
          fontFamily: 'monospace'
        }}>
          {params.value || 'Não informado'}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Criado em',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ 
          color: '#6b7280',
          fontWeight: 500,
          fontSize: '0.8rem'
        }}>
          {new Date(params.value).toLocaleDateString('pt-BR')}
        </Typography>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Ações',
      width: 150,
      getActions: (params) => {
        const userId = params.id as number;
        const isSyncing = syncingUserIds.has(userId);
        
        return [
          <GridActionsCellItem
            icon={isSyncing ? <CircularProgress size={18} /> : <SyncIcon />}
            label="Sincronizar"
            onClick={() => handleSyncUser(userId)}
            disabled={isSyncing || syncing}
            sx={{
              color: isSyncing ? '#9ca3af' : '#6b7280',
              '&:hover': {
                backgroundColor: 'rgba(107, 114, 128, 0.1)',
              },
              '&:disabled': {
                color: '#9ca3af',
              }
            }}
          />,
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Editar"
            onClick={() => handleEditUser(userId)}
            sx={{
              color: '#6b7280',
              '&:hover': {
                backgroundColor: 'rgba(107, 114, 128, 0.1)',
              }
            }}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Excluir"
            onClick={() => handleDeleteUser(userId)}
            disabled={!user?.isAdmin}
            sx={{
              color: user?.isAdmin ? '#dc2626' : '#9ca3af',
              '&:hover': {
                backgroundColor: user?.isAdmin ? 'rgba(220, 38, 38, 0.1)' : 'transparent',
              },
              '&:disabled': {
                color: '#9ca3af',
              }
            }}
          />,
        ];
      },
    },
  ];

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh', 
      bgcolor: 'background.default', 
      py: 2
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Gerenciar Usuários
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Gerencie usuários e sincronize assinaturas de email
        </Typography>
      </Box>

              <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ position: 'relative' }}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#9c27b0',
                  '&:hover': {
                    backgroundColor: '#7b1fa2',
                  },
                  '&:disabled': {
                    backgroundColor: '#f3e5f5',
                    color: 'rgba(0, 0, 0, 0.38)',
                  }
                }}
                startIcon={syncingGoogleWorkspace ? <CircularProgress size={20} color="inherit" /> : <CloudSyncIcon />}
                onClick={handleSyncGoogleWorkspace}
                disabled={syncingGoogleWorkspace}
              >
                {syncingGoogleWorkspace ? 'Sincronizando...' : 'Sincronizar Usuários'}
              </Button>
              {syncingGoogleWorkspace && (
                <LinearProgress
                  variant={googleWorkspaceProgress === -1 ? "indeterminate" : "determinate"}
                  value={googleWorkspaceProgress === -1 ? 0 : googleWorkspaceProgress}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    borderRadius: '0 0 4px 4px',
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#fff',
                    }
                  }}
                />
              )}
            </Box>
            
            <Box sx={{ position: 'relative' }}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#00bcd4',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#0097a7',
                  },
                  '&:disabled': {
                    backgroundColor: '#b2ebf2',
                    color: 'rgba(255, 255, 255, 0.7)',
                  }
                }}
                startIcon={syncingPhotos ? <CircularProgress size={20} color="inherit" /> : <PhotoCameraIcon />}
                onClick={handleSyncPhotos}
                disabled={syncingPhotos}
              >
                {syncingPhotos ? 'Sincronizando...' : 'Sincronizar Fotos'}
              </Button>
              {syncingPhotos && (
                <LinearProgress
                  variant={photosProgress === -1 ? "indeterminate" : "determinate"}
                  value={photosProgress === -1 ? 0 : photosProgress}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    borderRadius: '0 0 4px 4px',
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#fff',
                    }
                  }}
                />
              )}
            </Box>
            
            <Box sx={{ position: 'relative' }}>
              <Button
                variant="contained"
                startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                onClick={handleSyncAll}
                disabled={syncing}
                sx={{
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                  '&:disabled': {
                    backgroundColor: '#e3f2fd',
                    color: 'rgba(0, 0, 0, 0.38)',
                  }
                }}
              >
                {syncing ? 'Sincronizando...' : 'Sincronizar Assinaturas'}
              </Button>
              {syncing && (
                <LinearProgress
                  variant={syncProgress === -1 ? "indeterminate" : "determinate"}
                  value={syncProgress === -1 ? 0 : syncProgress}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    borderRadius: '0 0 4px 4px',
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#fff',
                    }
                  }}
                />
              )}
            </Box>
          </Box>
          
          <Tooltip title="Atualizar dados">
            <IconButton 
              onClick={loadUsers}
              disabled={loading}
              sx={{ 
                bgcolor: 'primary.main', 
                color: 'white',
                borderRadius: '50%',
                width: 40,
                height: 40,
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': {
                  bgcolor: '#e0e0e0',
                  color: '#bdbdbd'
                }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

      <Box sx={{ 
        height: 600, 
        width: '100%',
        backgroundColor: 'white',
        borderRadius: 3,
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
        border: '1px solid #f0f0f0'
      }}>
        <DataGrid
          rows={users}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            backgroundColor: 'transparent',
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8fafc',
              borderBottom: '2px solid #e2e8f0',
              fontWeight: 600,
              color: '#374151',
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              minHeight: '60px !important',
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
              },
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                justifyContent: 'center',
              },
              '& .MuiDataGrid-columnSeparator': {
                display: 'none',
              },
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f8f9fa',
              padding: '16px 20px',
              fontSize: '0.875rem',
              color: '#374151',
              fontWeight: 500,
            },
            '& .MuiDataGrid-row': {
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: '#f8fafc',
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #e2e8f0',
                },
              },
              '&:nth-of-type(even)': {
                backgroundColor: '#fafbfc',
              },
              '&:nth-of-type(odd)': {
                backgroundColor: 'white',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '2px solid #e2e8f0',
              backgroundColor: '#f8fafc',
              minHeight: '60px',
              '& .MuiTablePagination-root': {
                color: '#374151',
                fontWeight: 500,
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontWeight: 500,
                color: '#6b7280',
              },
            },
            '& .MuiDataGrid-virtualScroller': {
              backgroundColor: 'transparent',
            },
            '& .MuiDataGrid-virtualScrollerContent': {
              backgroundColor: 'transparent',
            },
            '& .MuiDataGrid-virtualScrollerRenderZone': {
              backgroundColor: 'transparent',
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:focus-within': {
              outline: 'none',
            },
          }}
        />
      </Box>

      {/* Modal de Edição */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleCancelEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Editar Usuário
          {editingUser && (
            <Typography variant="body2" color="text.secondary">
              {editingUser.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nome"
              value={editForm.name}
              onChange={(e) => handleEditFormChange('name', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Email"
              value={editForm.email}
              onChange={(e) => handleEditFormChange('email', e.target.value)}
              fullWidth
              required
              type="email"
            />
            <TextField
              label="Cargo"
              value={editForm.title}
              onChange={(e) => handleEditFormChange('title', e.target.value)}
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editForm.show_title}
                  onChange={(e) => handleEditFormChange('show_title', e.target.checked)}
                />
              }
              label="Exibir cargo na assinatura"
            />
            <TextField
              label="Telefone"
              value={editForm.mobile}
              onChange={(e) => handleEditFormChange('mobile', e.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Função</InputLabel>
              <Select
                value={editForm.role}
                label="Função"
                onChange={(e) => handleEditFormChange('role', e.target.value)}
              >
                <MenuItem value="user">Usuário</MenuItem>
                <MenuItem value="admin">Administrador</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} disabled={editLoading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained"
            disabled={editLoading || !editForm.name || !editForm.email}
          >
            {editLoading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sistema de notificações padronizado */}
      <NotificationSystem
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={notification.onClose || (() => {})}
      />
    </Box>
  );
};

export default Users;
